import React, { useMemo, useEffect } from 'react';
import { Branch } from '@/hooks/useBranches';
import { SuperChickenMenu } from './SuperChickenMenu';
import { ViewContentParams } from '@/types/metaPixel';
import { CartProvider } from '../contexts/CartContext';
import { usePublishedMenuLayout } from '@/hooks/useMenuBuilderLayout';
import { BlockBasedMenuRenderer } from '@/components/menu-builder/public/BlockBasedMenuRenderer';
import { Loader2 } from 'lucide-react';
import { MenuThemeProvider } from '@/contexts/MenuThemeContext';
import { FloatingCart } from '@/components/menu/FloatingCart';
import { MenuCustomization } from '@/types/menuCustomization';
import { FontLoader } from '@/components/common/FontLoader';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';
import { useTranslation } from 'react-i18next';

interface UnifiedMenuRendererProps {
  branch: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  onTrackViewContent?: (params: ViewContentParams) => void;
  isPreview?: boolean;
  showCart?: boolean;
  preloadedData?: PublicMenuData;
}

export const UnifiedMenuRenderer: React.FC<UnifiedMenuRendererProps> = ({ 
  branch, 
  enableRatings = false, 
  enableOrdering = false,
  onTrackViewContent,
  isPreview = false,
  showCart = true,
  preloadedData,
}) => {
  const { i18n } = useTranslation();
  
  // Apply branch's default language only for PUBLIC menu (not preview mode)
  // In preview mode (dashboard), we should keep the user's selected dashboard language
  useEffect(() => {
    if (!isPreview && branch.default_language && i18n.language !== branch.default_language) {
      i18n.changeLanguage(branch.default_language);
    }
  }, [branch.default_language, i18n, isPreview]);
  
  // Only fetch layout if NOT using preloaded data
  const { hasCustomLayout, isLoading, layout } = usePublishedMenuLayout(
    preloadedData ? '' : branch.id // Pass empty string to disable query when preloaded
  );

  // Use preloaded layout if available, otherwise fall back to hook
  const effectiveLayout = preloadedData?.layout || layout;
  const effectiveHasCustomLayout = !!effectiveLayout?.blocks;

  const customizationOverride = useMemo<Partial<MenuCustomization> | undefined>(() => {
    if (!effectiveLayout?.globalThemeOverrides) return undefined;
    
    const overrides = effectiveLayout.globalThemeOverrides;
    const result: any = { 
      components: { floatingCart: {} }, 
      colors: {}, 
      typography: {} 
    };
    
    // Cart Customization
    if (overrides.cartPosition) result.components.floatingCart.position = overrides.cartPosition;
    if (overrides.cartButtonColor) result.components.floatingCart.buttonColor = overrides.cartButtonColor;
    if (overrides.cartButtonStyle) result.components.floatingCart.buttonStyle = overrides.cartButtonStyle;
    
    // Global Colors
    if (overrides.primaryColor) result.colors.primary = overrides.primaryColor;
    if (overrides.textColor) result.colors.text = overrides.textColor;
    if (overrides.cardBackground) result.colors.surface = overrides.cardBackground;
    
    // Typography
    if (overrides.fontFamily) result.typography.fontFamily = overrides.fontFamily;
    if (overrides.fontWeight) {
      result.typography.headingWeight = overrides.fontWeight;
      result.typography.bodyWeight = overrides.fontWeight;
    }

    return result;
  }, [effectiveLayout]);

  if (isLoading && !preloadedData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <CartProvider branch={branch}>
      <MenuThemeProvider branch={branch} customizationOverride={customizationOverride}>
        <FontLoader fontFamily={effectiveLayout?.globalThemeOverrides?.fontFamily} />
        <div className="min-h-screen relative bg-gray-100">
          {effectiveHasCustomLayout ? (
            <BlockBasedMenuRenderer 
              branchId={branch.id} 
              branch={branch}
              enableRatings={enableRatings}
              enableOrdering={enableOrdering}
              preloadedData={preloadedData}
            />
          ) : (
            <SuperChickenMenu 
              branch={branch} 
              enableRatings={enableRatings} 
              enableOrdering={enableOrdering} 
              onTrackViewContent={onTrackViewContent}
              isPreview={isPreview}
              showCart={false}
              preloadedData={preloadedData}
            />
          )}
          
          {/* Shared Floating Cart for both renderers */}
          {enableOrdering && showCart && (
            <FloatingCart
              key={`cart-${i18n.language}`}
              branchId={branch.id}
              currency={branch.currency || 'USD'}
              isPreview={isPreview}
            />
          )}
        </div>
      </MenuThemeProvider>
    </CartProvider>
  );
};
